import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Play, Users, BookOpen, Globe, Award, GraduationCap, Clock, Target, CheckCircle, Calendar, MapPin } from 'lucide-react';
import { SEO, pageSEO } from '../components/SEO';

// Hero slides data
const heroSlides = [
  {
    id: 1,
    title: "One of a kind education for those who want to lead",
    highlight: "change.",
    subtitle: null,
    description: "Experience the power of hands-on learning, real-world internships, and collaborative connections with health professionals. CEPRES empowers you to unlock your true potential and create meaningful impact from day one.",
    cta: "Experience CIU",
    ctaLink: "/about",
    badges: null,
    image: "https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/an0m2tpw_WhatsApp%20Image%202026-02-04%20at%2017.35.14%20%286%29.jpeg"
  },
  {
    id: 2,
    subtitle: "High-quality, Affordable Education",
    title: "World-Class Online",
    highlight: "Programs",
    description: "Internationally recognized degrees through our innovative Digital Classroom. Experience flexible, interactive, and impactful education.",
    cta: "Learn More",
    ctaLink: "/programs",
    badges: null,
    image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1920"
  },
  {
    id: 3,
    subtitle: "Leading Research in Africa",
    title: "Research &",
    highlight: "Innovation Hub",
    description: "Join our research-driven academic community focused on applied sciences at the intersection of research, data, and technology.",
    cta: "Apply Now",
    ctaLink: "https://cnte.ciu.edu.lr/apply",
    badges: [
      { icon: 'calendar', text: 'Open Enrollment' },
      { icon: 'location', text: 'Liberia & Online' }
    ],
    image: "https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/rphc01ze_WhatsApp%20Image%202026-02-04%20at%2017.35.14%20%284%29.jpeg"
  },
  {
    id: 4,
    subtitle: "Cost-Effective & Flexible",
    title: "Hybrid Learning",
    highlight: "Model",
    description: "Graduate debt-free with our blended learning approach combining online education with traditional classroom instruction.",
    cta: "Explore Programs",
    ctaLink: "/programs",
    badges: null,
    image: "https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/3u1wed4j_WhatsApp%20Image%202026-02-04%20at%2017.35.14.jpeg"
  },
  {
    id: 5,
    title: "Admissions Open for",
    highlight: "2025 Intake",
    subtitle: null,
    description: "Join thousands of healthcare professionals who have advanced their careers with CEPRES International University. Applications are now open for January and May intakes.",
    cta: "Apply Now",
    ctaLink: "https://cnte.ciu.edu.lr/apply",
    badges: null,
    image: "https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/imabz1ag_WhatsApp%20Image%202026-02-04%20at%2017.35.13%20%283%29.jpeg"
  }
];

// Programs data
const featuredPrograms = [
  {
    id: 'bph',
    name: 'Bachelor of Public Health',
    shortName: 'BPH',
    description: 'A comprehensive program in epidemiology, biostatistics, and health policy for future public health leaders.',
    modules: ['Epidemiology', 'Biostatistics', 'Health Policy', 'Environmental Health', 'Community Health'],
    image: 'https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/mp7kzwiz_WhatsApp%20Image%202026-02-04%20at%2017.35.15%20%284%29.jpeg'
  },
  {
    id: 'bch',
    name: 'Bachelor of Community Health',
    shortName: 'BCH',
    description: 'Focus on community assessment, disease surveillance, and health education to improve community well-being.',
    modules: ['Community Assessment', 'Disease Surveillance', 'Health Education', 'Primary Healthcare', 'Nutrition'],
    image: 'https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/bnx3f2sc_WhatsApp%20Image%202026-02-04%20at%2017.35.13.jpeg'
  },
  {
    id: 'bsc-sa',
    name: 'BSc Sustainable Agriculture',
    shortName: 'BSc-SA',
    description: 'Learn sustainable farming, agroecology, and environmental conservation to feed future generations.',
    modules: ['Agroecology', 'Soil Science', 'Crop Production', 'Farm Management', 'Climate Agriculture'],
    image: 'https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/vfz74n9l_WhatsApp%20Image%202026-02-04%20at%2017.35.14%20%285%29.jpeg'
  }
];

// Stats data
const stats = [
  { number: '246+', label: 'Successful graduates working in healthcare', icon: Users },
  { number: '73%', label: 'of graduates employed within 6 months', icon: Target },
  { number: '7', label: 'Learning hubs across Liberia', icon: Globe },
];

// Testimonials
const testimonials = [
  {
    quote: "CEPRES has transformed my career. The flexible schedule allowed me to work while studying, and now I'm a certified public health professional.",
    author: "James K. Kollie",
    role: "BPH Graduate, 2023",
    image: "https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/ptyry2rh_WhatsApp%20Image%202026-02-04%20at%2017.35.12%20%283%29.jpeg"
  },
  {
    quote: "The practical, hands-on approach at CEPRES prepared me for real-world healthcare challenges. The faculty support was exceptional.",
    author: "Mary T. Weah",
    role: "BCH Graduate, 2022",
    image: "https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/vfz74n9l_WhatsApp%20Image%202026-02-04%20at%2017.35.14%20%285%29.jpeg"
  }
];

// Intakes
const intakes = [
  {
    month: 'January',
    year: '2026',
    status: 'Applications open and ongoing.',
    deadline: '1st November, 2025'
  },
  {
    month: 'May',
    year: '2026',
    status: 'Applications open and ongoing.',
    deadline: '1st March 2026'
  }
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div data-testid="home-page" className="min-h-screen">
      <SEO {...pageSEO.home} url="/" />
      {/* Hero Slider Section - Enhanced Style */}
      <section data-testid="hero-section" className="relative h-screen min-h-[700px]">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <img
                src={slide.image}
                alt=""
                className="w-full h-full object-cover"
              />
              {/* Gradient overlay - CEPRES brand colors */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0D4A2E] via-[#0D4A2E]/90 to-[#0D4A2E]/60" />
              <div className="absolute inset-0 pattern-overlay" />
            </div>

            {/* Slide Counter */}
            <div className={`absolute top-28 left-4 lg:left-8 z-20 ${
              index === currentSlide ? 'animate-fade-in-up' : ''
            }`}>
              <span className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                {String(index + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}
              </span>
            </div>

            {/* Content */}
            <div className="relative h-full max-w-7xl mx-auto px-4 lg:px-8 flex items-center">
              <div className="max-w-2xl pt-20">
                {/* Subtitle */}
                {slide.subtitle && (
                  <p className={`text-[#C5A028] text-lg font-medium mb-4 ${
                    index === currentSlide ? 'animate-fade-in-up' : ''
                  }`}>
                    {slide.subtitle}
                  </p>
                )}
                
                {/* Main Title */}
                <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 ${
                  index === currentSlide ? 'animate-fade-in-up' : ''
                }`}>
                  {slide.title}
                  <br />
                  <span className="text-[#C5A028]">{slide.highlight}</span>
                </h1>
                
                {/* Description */}
                <p className={`text-white/80 text-lg md:text-xl mb-8 leading-relaxed ${
                  index === currentSlide ? 'animate-fade-in-up animation-delay-200' : ''
                }`}>
                  {slide.description}
                </p>
                
                {/* Badges */}
                {slide.badges && (
                  <div className={`flex flex-wrap gap-4 mb-8 ${
                    index === currentSlide ? 'animate-fade-in-up animation-delay-200' : ''
                  }`}>
                    {slide.badges.map((badge, i) => (
                      <div key={i} className="flex items-center gap-2 text-white/90 text-sm">
                        {badge.icon === 'calendar' && <Calendar className="w-4 h-4" />}
                        {badge.icon === 'location' && <MapPin className="w-4 h-4" />}
                        <span>{badge.text}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* CTA Button */}
                {slide.ctaLink.startsWith('http') ? (
                  <a
                    href={slide.ctaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-3 bg-[#0D4A2E] border-2 border-[#C5A028] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#C5A028] hover:text-[#0D4A2E] transition-all duration-300 ${
                      index === currentSlide ? 'animate-fade-in-up animation-delay-300' : ''
                    }`}
                  >
                    <ChevronRight className="w-5 h-5" />
                    {slide.cta}
                  </a>
                ) : (
                  <Link 
                    to={slide.ctaLink}
                    className={`inline-flex items-center gap-3 bg-[#0D4A2E] border-2 border-[#C5A028] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#C5A028] hover:text-[#0D4A2E] transition-all duration-300 ${
                      index === currentSlide ? 'animate-fade-in-up animation-delay-300' : ''
                    }`}
                  >
                    <ChevronRight className="w-5 h-5" />
                    {slide.cta}
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Slide Navigation */}
        <div className="absolute bottom-10 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between">
            {/* Slide Indicators */}
            <div className="flex gap-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentSlide(index);
                  }}
                  className={`w-12 h-1 transition-all duration-300 ${
                    index === currentSlide ? 'bg-[#C5A028]' : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
            
            {/* Arrow Controls */}
            <div className="flex gap-2">
              <button 
                onClick={prevSlide}
                className="p-3 border border-white/30 text-white hover:bg-white hover:text-[#0D4A2E] transition-all duration-300"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={nextSlide}
                className="p-3 border border-white/30 text-white hover:bg-white hover:text-[#0D4A2E] transition-all duration-300"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - ALU Style T letter */}
      <section data-testid="about-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex gap-6">
                <span className="text-[150px] font-bold text-[#0D4A2E] leading-none">C</span>
                <div className="pt-8">
                  <p className="text-[#666666] text-lg leading-relaxed">
                    CEPRES International University provides higher education for a higher purpose. 
                    Founded during the 2014 Ebola crisis, CIU is dedicated to equipping graduates 
                    with the skills, acuity, and vision needed to succeed as ethical, healthcare leaders.
                  </p>
                  <p className="text-[#666666] text-lg leading-relaxed mt-4">
                    Our unique accelerated intensive learning model equips graduates with practical skills, 
                    and our vibrant community, fostered through Pan-African programs, promises lifelong 
                    friendship and inspiration.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#C5A028] p-8 lg:p-10">
                <h3 className="text-2xl font-bold text-white mb-4">2024 Impact Report</h3>
                <p className="text-white/90 mb-6">
                  Shaping Liberia's Healthcare Leaders One Year At A Time. Download our 2024 Impact Report
                </p>
                <Link 
                  to="/about"
                  className="inline-flex items-center gap-2 bg-white text-[#0D4A2E] px-6 py-3 font-semibold hover:bg-[#F5F5F5] transition-colors duration-300"
                >
                  <ArrowRight className="w-4 h-4" />
                  Read More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section - ALU Style Cards */}
      <section data-testid="programs-section" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0D4A2E] mb-4">
            Bachelor's <span className="text-[#C5A028]">Degree</span> Programs
          </h2>
          
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Left Image */}
            <div className="lg:col-span-4 relative h-64 lg:h-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/yfrf73c9_WhatsApp%20Image%202026-02-04%20at%2017.35.14%20%287%29.jpeg" 
                alt="CEPRES Students at Event" 
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Program Cards */}
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 divide-x divide-[#E5E5E5]">
              {featuredPrograms.map((program) => (
                <div key={program.id} className="bg-white p-6 lg:p-8">
                  <h3 className="text-xl font-bold text-[#0D4A2E] mb-2">
                    {program.shortName}
                  </h3>
                  <h4 className="text-lg font-semibold text-[#C5A028] mb-4">
                    {program.name}
                  </h4>
                  <p className="text-[#666666] text-sm mb-6 leading-relaxed">
                    {program.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {program.modules.slice(0, 4).map((module, i) => (
                      <li key={i} className="text-sm text-[#666666] border-b border-[#E5E5E5] pb-2">
                        {module}
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to={`/programs#${program.id}`}
                    className="inline-flex items-center gap-2 text-[#C5A028] font-semibold text-sm uppercase tracking-wider hover:gap-4 transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Learn More
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - ALU Style */}
      <section data-testid="testimonials-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="flex gap-6 p-8 bg-[#F5F5F5]">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.author}
                  className="w-20 h-20 object-cover rounded-full flex-shrink-0"
                />
                <div>
                  <p className="text-[#666666] italic mb-4 leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  <p className="font-bold text-[#0D4A2E]">{testimonial.author}</p>
                  <p className="text-sm text-[#C5A028]">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Message from the President */}
      <section data-testid="president-section" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/jcz8dn4g_WhatsApp%20Image%202026-02-04%20at%2017.35.13%20%281%29.jpeg"
                alt="Dr. Mogana S. Flomo, Jr."
                className="w-full max-w-md mx-auto shadow-xl"
              />
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <p className="text-[#C5A028] font-medium uppercase tracking-wider mb-2">Message from the President</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0D4A2E] mb-6">
                "Education is the foundation of <span className="text-[#C5A028]">transformation</span>"
              </h2>
              <p className="text-[#666666] leading-relaxed mb-6">
                At CEPRES International University, we believe that quality education should be accessible to all. 
                Our mission is to equip the next generation of healthcare professionals and agricultural leaders 
                with the skills, knowledge, and ethical foundation needed to transform our communities.
              </p>
              <p className="text-[#666666] leading-relaxed mb-8">
                Having spent over 28 years in education and served as Liberia's Minister of Agriculture, 
                I understand the critical role that trained professionals play in national development. 
                Join us in this journey of learning and impact.
              </p>
              <div className="border-l-4 border-[#C5A028] pl-4">
                <p className="font-bold text-[#0D4A2E]">Prof. Dr. Mogana S. Flomo, Jr.</p>
                <p className="text-sm text-[#666666]">Founder & President, CEPRES International University</p>
                <p className="text-sm text-[#666666]">Former Minister of Agriculture, Republic of Liberia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes CIU Unique - ALU Style Stats */}
      <section data-testid="unique-section" className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                What makes <span className="text-[#C5A028]">CIU</span> Programs Unique?
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                In our experience, the best learning happens during hands-on, real-world experiences. 
                That's why we make internships and field practice a core part of the CIU curriculum. 
                Our graduates are prepared to take on fulfilling careers the moment they graduate.
              </p>
              <Link 
                to="/programs"
                className="inline-flex items-center gap-2 text-[#C5A028] font-semibold uppercase tracking-wider hover:gap-4 transition-all duration-300"
              >
                <ArrowRight className="w-5 h-5" />
                STUDY AT CIU
              </Link>
            </div>
            
            <div className="space-y-8">
              {[
                { title: 'Learn by Doing', desc: 'Our learning model is heavily focused on learning in the real world.', stat: '246', label: 'of our students have launched successful health initiatives' },
                { title: 'Scale your Impact', desc: 'We are committed to raising ethical leaders by 2030', stat: '73%', label: 'of our graduates are employed or have started their own initiatives' },
                { title: 'Flexible Scheduling', desc: 'Education that works for working professionals', stat: '3', label: 'Learning tracks: 2, 3, or 4 years' },
              ].map((item, index) => (
                <div key={index} className="flex gap-6 items-start">
                  <div className="w-24 h-24 rounded-full bg-white/10 flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-3xl font-bold text-[#C5A028]">{item.stat}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-white/60 text-sm">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Intakes - ALU Style */}
      <section data-testid="intakes-section" className="py-20 bg-gradient-to-r from-[#0D4A2E] to-[#156B42] text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Image */}
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/bnx3f2sc_WhatsApp%20Image%202026-02-04%20at%2017.35.13.jpeg" 
                alt="Graduates"
                className="w-full h-auto"
              />
            </div>
            
            {/* Right - Intake Info */}
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-4">
                Begin your bachelor's degree <span className="text-[#C5A028]">journey to CIU</span>
              </h2>
              <p className="text-white/80 mb-10">
                Start your application to CIU through any of the following intakes.
              </p>
              
              <div className="space-y-4">
                {intakes.map((intake, index) => (
                  <div key={index} className="bg-white p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-2xl font-bold text-[#0D4A2E]">{intake.month}</h3>
                        <p className="text-[#C5A028] font-semibold">{intake.year} Intake</p>
                      </div>
                    </div>
                    <p className="text-[#666666] mt-2">{intake.status}</p>
                    <p className="text-[#C5A028] text-sm mt-2">
                      <span className="font-semibold">Applications close</span><br />
                      {intake.deadline}
                    </p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-4 bg-white/10 border-l-4 border-[#C5A028]">
                <p className="text-sm text-white/90">
                  <strong>IMPORTANT:</strong> CIU does not ask for money or any reward or work with agents 
                  that ask for money to help applicants fill out their applications.
                </p>
              </div>
              
              <a 
                href="https://cnte.ciu.edu.lr/apply"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#D4B43D] transition-colors duration-300"
              >
                <ArrowRight className="w-5 h-5" />
                APPLY NOW
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA - ALU Style */}
      <section data-testid="faq-cta-section" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="flex items-center gap-6">
              <span className="text-6xl font-bold text-[#C5A028]">?</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0D4A2E]">
                Do you have more questions about CIU bachelor's degree?
              </h2>
            </div>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-300"
            >
              <ArrowRight className="w-5 h-5" />
              Frequently Asked Questions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
